<template lang="pug">
.itemAbstractComponent(:class="`_${variant}`")
  .itemWrapper(
    :class="wrapperClasses"
    @click.stop="onItemClick"
  )
    .imageBackground
      .imageWrapper
        ui-image.image(
          :image-url="userItem.data.imageUrl"
          :has-absolute-url="true"
          :class="`_${variant}Size`"
        )
      .bookmark(
        v-if="showFavorite"
        @click.stop="onBookmarkClick"
      )
        ui-svg-icon(
          :name="userItem.favorite ? 'bookmark-full' : 'bookmark-empty'"
          :original-colors="true"
        )

    slot.content

    .buttonWrapper(v-if="showActionButton")
      ui-button.button(
        v-if="!userItem.bought"
        size="xs"
        color="blue-outline"
        is-responsive
        @click.stop="onBuyClick"
      ) {{ userItem.purchaseText }}
      ui-button.button(
        v-else-if="userItem.bought"
        size="xs"
        color="green"
        is-responsive
        @click.stop="onActionClick"
      ) {{ labelActionButton }}
    .buttonWrapper(v-if="showBuyButton")
      template(v-if="isInBasket")
        .textBasket Уже в корзине
      template(v-else)
        ui-price(
          v-if="!userItemIsBought"
          variant="button"
          text="В корзину за"
          :price="userItem.price"
          :old-price="userItem.oldPrice"
          @buttonClick="onBuyClick"
          @click.stop
        )
      template(v-if="subscription?.isDemo && !userItemIsBought")
        .text Демо-доступ до {{ subscription.expires }}
      template(v-else-if="userItemIsBought")
        .text Подписка активна до {{ userItemExpires }}
  .info(
    v-if="showInfo"
    @click="onInfoClick"
  )
    ui-svg-icon(
      name="info"
      :size="23"
    )
</template>

<script>
import UserItem from '@/models/UserItem'
import SetFavoriteController from '@/controllers/SetFavoriteController'
import { computed, inject } from 'vue'
import moment from 'moment'
export default {
  props: {
    userItem: {
      type: UserItem,
      required: true
    },
    withShadow: Boolean,
    showFavorite: Boolean,
    showInfo: Boolean,
    showActionButton: Boolean,
    showBuyButton: Boolean,
    labelActionButton: {
      type: String,
      default: 'Заниматься'
    },
    // default || catalog
    variant: {
      type: String,
      default: 'default'
    },
    isClickable: {
      type: Boolean,
      default: true
    },
    isInBasket: Boolean
  },
  emits: [
    'action:favorite-off',
    'action:favorite-on',
    'action:click-on-item',
    'action:click-on-info',
    'action:click-on-buy',
    'action:click-on-action'
  ],
  setup (props) {
    const store = inject('store')
    const subscription = computed(() => store.user.profile?.subscription)
    const subscriptions = computed(() => store.user.profile?.subscriptions)
    const { request } = SetFavoriteController()
    const setFavorite = async () => {
      await request(props.userItem, !props.userItem.favorite)
    }

    const subscriptionsItem = computed(() =>
      subscriptions.value?.find(el => el.nomenklatura === props.userItem.nomenklatura)
    )
    const userItemIsBought = computed(() => subscriptionsItem.value?.bought)
    const userItemExpires = computed(() => {
      return subscriptionsItem.value?.expires
        ? moment(subscriptionsItem.value?.expires).format('DD.MM.YYYY')
        : null
    })

    return {
      setFavorite,
      userItemIsBought,
      subscription,
      subscriptions,
      userItemExpires
    }
  },
  computed: {
    wrapperClasses () {
      return [{ _withShadow: this.withShadow }, { _clickable: this.isClickable }]
    }
  },
  methods: {
    onInfoClick () {
      this.$emit('action:click-on-info')
    },
    onActionClick () {
      this.$emit('action:click-on-action')
    },
    onBuyClick () {
      this.$emit('action:click-on-buy')
    },
    async onBookmarkClick () {
      await this.setFavorite()
      if (this.userItem.favorite) {
        this.$emit('action:favorite-on')
      } else {
        this.$emit('action:favorite-off')
      }
    },
    onItemClick () {
      if (!this.isClickable) return
      this.$emit('action:click-on-item')
    }
  }
}
</script>

<style lang="sass" scoped>
.itemAbstractComponent
  width: 100%
  height: 100%
  display: flex
  flex-flow: column

  > .itemWrapper
    flex: 1
    height: 100%
    width: 100%
    display: flex
    flex-direction: column
    background-color: $cWhite
    border-radius: $brL
    padding: 5*$u

    &._clickable
      cursor: pointer

    &._withShadow
      box-shadow: 0 1.5*$u $cDarkBackground

    > .imageBackground
      height: fit-content
      position: relative
      border-radius: $brM
      background-color: $cElements
      margin-bottom: 5*$u

      > .imageWrapper
        border-radius: 2.5*$u
        padding: 5*$u

        > .image
          @include aspect-ratio(3, 4)
          margin: 0 auto

          // IMAGE SIZE PER SCREEN
          &._defaultSize
            max-width: 52*$u
            @media screen and (max-width: $padWidth)
              max-width: 38*$u

          &._catalogSize
            max-width: 33*$u
            @media screen and (max-width: $padWidth)
              max-width: 41*$u

      > .bookmark
        width: 7.5*$u
        height: 7.5*$u
        position: absolute
        top: 0
        right: 0
        border-radius: 1.5*$u
        background-color: $cWhite
        padding: 1.5*$u 2*$u
        cursor: pointer

    > .buttonWrapper
      margin-top: 3*$u
      .textBasket
        white-space: nowrap
        cursor: default
        height: 11.5*$u
        padding: 0 3*$u
        display: flex
        width: 32*$u
        justify-content: center
        align-items: center
        background-color: $cHtBlue
        color: $cDarkGrey
        border-radius: 2.5*$u
      .button
        ~ .text
          margin-top: 3*$u
      .text
        color: $cDarkGrey
        @include font('t16-regular')
        + .text
          margin-top: 3*$u

  &._catalog
    > .itemWrapper
      padding: 4*$u
      > .imageWrapper
        padding: 4*$u

  > .info
    flex: 0 0 10*$u
    width: 12.5*$u
    height: 10*$u
    display: flex
    align-items: center
    justify-content: center
    border-radius: 0 0 2.5*$u 2.5*$u
    background-color: $cElements
    color: $cBlue
    padding: auto
    margin-left: 75%
    cursor: pointer
</style>
